import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Banknote,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  CloudUpload,
  Eye,
  Image as ImageIcon,
  PencilLine,
  Plus,
  RotateCcw,
  Save,
  Scale,
  Ship,
  ShoppingCart,
  Table2,
  Trash2,
  Truck,
  User,
  X,
} from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  CustomDatePicker,
  CustomMonthPicker,
  CustomSelect,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
} from '../components/common';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import {
  createDailyEntry,
  deleteDailyEntry,
  fetchDailyEntries,
  updateDailyEntry,
} from '../services/dailyEntryService';
import { convertImageFileToWebpDataUrl } from '../utils/imageConverter';
import { fetchLansas } from '../services/lansaService';
import { fetchSuppliers } from '../services/supplierService';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAILY_ENTRIES_PAGE_SIZE = 20;
const BANK_SUGGESTIONS = [
  'AUB',
  'BDO',
  'BPI',
  'CHINABANK',
  'EASTWEST',
  'LANDBANK',
  'METROBANK',
  'PNB',
  'PSBANK',
  'RCBC',
  'SECURITY BANK',
  'UNIONBANK',
  '1st Valley Bank, Inc. A Development Bank',
  'Advance Credit Bank (A Rural Bank) Corp.',
  'Agribusiness Banking Corporation - A Rural Bank',
  'Agricultural Bank of the Philippines, Inc. (A Rural Bank)',
  'Al-Amanah Islamic Investment Bank of the Philippines',
  'Aliaga Farmers Rural Bank, Inc.',
  'AllBank (A Thrift Bank), Inc.',
  'Anilao Bank (Rural Bank of Anilao (Iloilo), Inc.',
  'ARDCIBank, Inc., A Rural Bank',
  'ASENSO Rural Bank of Bautista, Inc.',
  'Asia United Bank Corporation',
  'Aspac Rural Bank, Inc.',
  'AuroraBank (A Rural Bank), Inc.',
  'Australia and New Zealand Banking Group Limited',
  'Baclaran Rural Bank, Inc.',
  'Balanga Rural Bank, Inc.',
  'Baliuag Rural Bank, Inc.',
  'Banco Abucay, Inc.',
  'Banco Bakun, Inc. (A Rural Bank)',
  'Banco Cooperativa De Zamboanga',
  'Banco De Calamba, Inc. (A Rural Bank)',
  'Banco de Mindoro, Inc. (A Rural Bank)',
  'Banco Mexico, Inc. (A Rural Bank)',
  'Banco San Jose (Camsur), Inc. – A Rural Bank',
  'Banco San Vicente, Inc. (A Rural Bank)',
  'Banco Santiago De Libon, Inc., (A Rural Bank)',
  'Banco Sual (A Rural Bank), Inc.',
  'Bangko Kabayan, Inc. (A Private Development Bank)',
  'Bangko Kaisa, Inc. (Formerly: Rural Bank of Bugasong (Antique), Inc.)',
  'Bangko Mabuhay (A Rural Bank), Inc.',
  'Bangko Magsaysay (Isabela), Inc. - A Rural Bank',
  'Bangko Montañosa, Inc. (A Rural Bank)',
  'Bangko ng Kabuhayan, (A Rural Bank) Inc.',
  'Bangko Nuestra Senora Del Pilar, Inc. (A Rural Bank)',
  'Bangko Pasig (Rural Bank)',
  'Bangko Rural ng San Teodoro, Inc.',
  'Bangkok Bank Public Company Limited',
  'Bank of America, N.A.',
  'Bank of China (Hongkong) Limited-Manila Branch',
  'Bank of Commerce',
  'Bank of Hope, Inc. (A Rural Bank)',
  'Bank of Makati (A Savings Bank), Inc.',
  'Bank of Ormoc, Inc. (A Rural Bank)',
  'Bank of the Philippine Islands',
  'Bank One Savings Corporation',
  'Banko Zambaleño Inc., (BZI) (A Rural Bank)',
  'Bankways, Inc. (A Rural Bank)',
  'Bannawag Bank, Inc. (A Rural Bank)',
  'Bataan Development Bank',
  'Batangas Rural Bank for Cooperatives, Inc.',
  'Bayanihan Bank, Inc',
  'Baybank, Inc. (A Rural Bank)',
  'BDO Network Bank, Inc.',
  'BDO Private Bank, Inc.',
  'BDO Unibank, Inc.',
  'Benguet Center Bank, Inc. A Rural Bank',
  'BHF Rural Bank, Inc.',
  'Biñan Rural Bank, Inc.',
  'BINHI Rural Bank, Inc. (Formerly: Rural Bank of Balingasag (Misamis Oriental), Inc.)',
  'BOF, Inc. (A Rural Bank)',
  'Bolbok Rural Bank, Inc.',
  'BPI Direct BanKO, Inc., A Savings Bank',
  'BRIC Bank, Inc. A Rural Bank',
  'Bukidnon Bank, Inc. (A Rural Bank)',
  'Butuan City Rural Bank, Inc.',
  'Cagsawa Rural Bank, Inc.',
  'Camalig Bank, Inc. (A Rural Bank)',
  'Camiling Rural Bank, Inc.',
  'Cantilan Bank, Inc. (A Rural Bank)',
  'Card Bank, Inc. (A Microfinance - Oriented Rural Bank)',
  'CARD MRI Rizal Bank, Inc., A Microfinance-Oriented Rural Bank',
  'CARD SME Bank, Inc. A Thrift Bank',
  'Caritas Banco ng Masa, Inc. (A Rural Bank)',
  'Cathay United Bank Co., LTD. - Manila Branch',
  'Cavite United Rural Bank, Corporation',
  'Cebuana Lhuillier Rural Bank, Inc.',
  'Century Rural Bank, Inc.',
  'Century Savings Bank Corporation',
  'Chang Hwa Commercial Bank, Ltd. - Manila Branch',
  'China Bank Savings, Inc.',
  'China Banking Corporation',
  'CIMB Bank Philippines Inc.',
  'Citibank, N.A.',
  'Citizen\'s Rural Bank (Cabiao), Inc.',
  'City Savings Bank, Inc.',
  'Citystate Savings Bank, Inc.',
  'Classic Rural Bank, Inc.',
  'Common Wealth Rural Bank, Inc.',
  'Community Rural Bank of Dapitan City, Incorporated',
  'Community Rural Bank of San Gabriel (La Union), Inc.',
  'Cooperative Bank of Benguet (CBB)',
  'Cooperative Bank of Cagayan',
  'Cooperative Bank of Cebu',
  'Cooperative Bank of Cotabato',
  'Cooperative Bank of La Union',
  'Cooperative Bank of Mt. Province',
  'Cooperative Bank of Negros Oriental',
  'Cooperative Bank of Nueva Vizcaya',
  'Cooperative Bank of Palawan',
  'Cooperative Bank of Quezon Province',
  'Cooperative Bank of Zambales',
  'Cordillera Bank, Inc. (A Rural Bank)',
  'Cordillera Savings Bank, Inc.',
  'Crown Bank, Inc. (A Rural Bank)',
  'CSFirst Bank, INC. -A Rural Bank',
  'CTBC Bank (Philippines) Corporation',
  'Cuyapo Rural Bank, Inc.',
  'Deutsche Bank AG',
  'Development Bank of the Philippines',
  'Diamond Rural Bank, Inc.',
  'Dumaguete City Development Bank, Inc.',
  'Dumaguete Rural Bank, Inc.',
  'Dungganon Bank (A Rural Bank), Inc.',
  'East West Banking Corporation',
  'East West Rural Bank, Inc.',
  'ECCOBANK (A Rural Bank) Inc.',
  'Enterprise Bank, Inc. (A Rural Bank)',
  'Enterprise Capital Bank, Inc. (Taguig Rural Bank)',
  'Entrepreneur Rural Bank, Inc.',
  'Equicom Savings Bank, Inc.',
  'Far Eastern Bank (A Rural Bank) Inc.',
  'Farm Bank, Inc. (A Rural Bank)',
  'FBC First Bank of Calaca, Inc.',
  'Filidian Rural Bank of Antipolo Inc.',
  'First Commercial Bank, Ltd., Manila Branch',
  'First Community Bank, Inc. (A Rural Bank)',
  'First Consolidated Bank, Inc. (A Private Development Bank)',
  'First Imperial Business Bank, Inc. (A Rural Bank)',
  'First Integrity Bank, Inc. (Rural Bank of Bailen)',
  'First Isabela Cooperative Bank, Inc. (Ficobank)',
  'First Mindoro Microfinance Rural Bank, Inc.',
  'First Philippine Partners Bank, Inc. (A Rural Bank)',
  'First State Bank, Inc., A Rural Bank',
  'First TruBank (A Rural Bank), Inc.',
  'First United Farmers Rural Bank, Inc.',
  'Frontier Rural Bank, Inc.',
  'Gateway Rural Bank Incorporated',
  'GM Bank of Luzon, Inc. (A Rural Bank)',
  'Golden Rural Bank of the Philippines, Inc.',
  'GoTyme Bank Corporation',
  'Guagua Rural Bank, Inc.',
  'Guagua Savers Bank (A Rural Bank), Inc.',
  'Highland Rural Bank, Inc.',
  'Hiyas Banking Corporation (A Thrift Bank)',
  'Hua Nan Commercial Bank, Ltd. - Manila Branch',
  'Ilocos Consolidated Cooperative Bank',
  'Imus Rural Bank, Inc.',
  'Industrial and Commercial Bank of China Limited - Manila Branch',
  'Industrial Bank of Korea Manila Branch',
  'ING Bank N.V.',
  'Innovative Bank  Inc. (A Rural Bank)',
  'ISLA Bank (A Thrift Bank), Inc.',
  'Janiuay Rural Bank, Inc.',
  'JP Morgan Chase Bank, N.A.',
  'Katipunan Banking Corporation (A Rural Bank)',
  'KEB Hana Bank - Manila Branch',
  'Key Rural Bank, Inc.',
  'Lagawe Highlands Rural Bank, Inc.',
  'Laguna Prestige Banking Corporation, (A Rural Bank)',
  'Land Bank of the Philippines',
  'Legazpi Savings Bank, Inc.',
  'Lemery Savings and Loan Bank, Inc.',
  'Liberty Bank (A Rural Bank), Inc.',
  'Life Savings Bank, Inc.',
  'LifeBank- A Rural Bank',
  'Limcoma Rural Bank, Inc.',
  'Lipa Bank, Inc. (A Rural Bank)',
  'LOLC Bank Philippines Incorporated (A Thrift Bank)',
  'LUDB Bank, Inc. (A Rural Bank)',
  'Luzon Development Bank',
  'Mactan Rural Bank (Lapu-Lapu City), Inc.',
  'Maharlika Rural Bank, Inc.',
  'Makiling Development Bank Corporation',
  'Malarayat Rural Bank, Inc.',
  'Malayan Savings Bank, Inc.',
  'Mallig Plains Rural Bank (Isabela), Inc.',
  'Manor Bank - A Rural Bank Corp.',
  'Marayo Bank, Inc. (A Rural Bank)',
  'Maribank Philippines, Inc. (A Rural Bank)',
  'Maritime Bank Corporation (A Rural Bank)',
  'Masagana Rural Bank, (Gen. Natividad, N.E.), Inc.',
  'Maya Bank, Inc.',
  'Maybank Philippines, Incorporated',
  'Mega International Commercial Bank Co., Ltd.',
  'Mega Rural Bank (Lucena City), Inc.',
  'Metropolitan Bank and Trust Company',
  'Mindanao Consolidated Cooperative Bank',
  'Misamis Bank, Inc. A Rural Bank',
  'Mizuho Bank, Ltd. - Manila Branch',
  'Money Mall Rural Bank, Inc.',
  'Mount Carmel Rural Bank, Inc.',
  'MUFG Bank, Ltd.',
  'MVSM Bank (A Rural Bank Since 1953) Inc.',
  'National Teachers & Employees Cooperative Bank',
  'Negros Cooperative Bank',
  'Netbank (A Rural Bank), Inc.',
  'Network Consolidated Cooperative Bank',
  'New Rural Bank of Agoncillo, Inc.',
  'New Rural Bank of San Leonardo (Nueva Ecija), Inc.',
  'North Pacific Banking Corporation (A Rural Bank)',
  'Northpoint Development Bank, Inc.',
  'Occidental Mindoro Cooperative Bank (OMCB)',
  'One Cooperative Bank',
  'One Unified Rural Bank of Cavite Incorporated',
  'Ormon Bank (Rural Bank of Mulanay (Quezon), Inc.)',
  'Overseas Filipino Bank, Inc., A Digital Bank of LANDBANK',
  'Own Bank, The Rural Bank of Cavite City, Inc.',
  'Pacific Ace Savings Bank, Inc.',
  'Pampanga Development Bank',
  'Pangasinan Bank (A Rural Bank), Inc.',
  'Panguil Bay Rural Bank Of Ozamiz, Inc.',
  'Partner Rural Bank (Cotabato), Inc.',
  'Penbank, Inc. (A Private Development Bank) (formerly: Peninsula Rural Bank, Inc.)',
  'People’s Hawak Kamay Bank, Inc.',
  'People\'s Rural Bank (Gen. Santos City), Inc.',
  'Phil Star Development Bank, Inc.',
  'Philippine Bank of Communications',
  'Philippine Business Bank, Inc., A Savings Bank',
  'Philippine National Bank',
  'Philippine Savings Bank',
  'Philippine SME Bank, Inc., A Rural Bank',
  'Philippine Trust Company',
  'Philippine Veterans Bank',
  'Pioneer Bank (A Rural Bank), Inc.',
  'Planbank "Rural Bank of Canlubang Planters, Inc."',
  'Producers Savings Bank Corporation',
  'ProFarmers Rural Banking Corporation',
  'Progressive Rural Bank, Inc.',
  'Provident Rural Bank of Sta. Cruz (Laguna), Inc.',
  'Queen City Development Bank, Inc. or Queenbank, A Thrift Bank',
  'Quezon Capital Rural Bank, Inc.',
  'Quezon Coconut Bank, Inc. (A Rural Bank)',
  'Rang-Ay Bank (A Rural Bank), Inc.',
  'RBT Bank, Inc., A Rural Bank',
  'RCBC Microbank - A Thrift Bank Inc.',
  'Rizal Commercial Banking Corporation',
  'RNG Bank',
  'Rural Bank of Alabel (Sarangani), Inc.',
  'Rural Bank of Alaminos (Pangasinan), Inc.',
  'Rural Bank of Alicia (Isabela), Inc.',
  'Rural Bank of Alitagtag, Inc.',
  'Rural Bank of Altavas (Aklan), Inc.',
  'Rural Bank of Amlan (Negros Oriental), Inc.',
  'Rural Bank of Anda (Pangasinan), Inc.',
  'Rural Bank of Angat (Bulacan), Inc.',
  'Rural Bank of Angeles, Inc.',
  'Rural Bank of Angono, Inc.',
  'Rural Bank of Antipolo, Inc.',
  'Rural Bank of Apalit, Inc.',
  'Rural Bank of Aritao, Inc.',
  'Rural Bank of Bacnotan, Inc.',
  'Rural Bank of Baco (Oriental Mindoro), Inc.',
  'Rural Bank of Bacolod City, Inc.',
  'Rural Bank of Bacong (Negros Oriental), Inc.',
  'Rural Bank of Bagabag (N.V.), Inc.',
  'Rural Bank of Bagac, Bataan, Inc.',
  'Rural Bank of Baguio, Inc.',
  'Rural Bank of Balungao (Pangasinan), Inc.',
  'Rural Bank of Bambang (Nueva Vizcaya), Inc.',
  'Rural Bank of Banga (Aklan), Inc.',
  'Rural Bank of Bansud (Oriental Mindoro), Inc.',
  'Rural Bank of Barili (Cebu), Inc.',
  'Rural Bank of Batac, Inc.',
  'Rural Bank of Bato (Leyte), Inc.',
  'Rural Bank of Bauang, Inc.',
  'Rural Bank of Bay, Inc.',
  'Rural Bank of Bayambang (Pangasinan), Inc.',
  'Rural Bank of Bayombong, Inc.',
  'Rural Bank of Bogo (Cebu), Inc.',
  'Rural Bank of Bolinao (Pangasinan), Inc.',
  'Rural Bank of Bonifacio (Misamis Occidental), Inc.',
  'Rural Bank of Bontoc (Mountain Province), Inc.',
  'Rural Bank of Brooke\'S Point (Palawan), Inc.',
  'Rural Bank of Bucay (Abra), Inc.',
  'Rural Bank of Cabangan (Zambales), Inc.',
  'Rural Bank of Cabatuan (Iloilo), Inc.',
  'Rural Bank of Cabugao (Ilocos Sur), Inc.',
  'Rural Bank of Cadiz, Inc.',
  'Rural Bank of Calauan, Inc.',
  'Rural Bank of Calbayog City, Inc.',
  'Rural Bank of Calinog (Iloilo), Inc.',
  'Rural Bank of Candelaria, Inc.',
  'Rural Bank of Capalonga (Camarines Norte),Inc.',
  'Rural Bank of Cardona (Rizal), Inc.',
  'Rural Bank of Cauayan, Incorporated',
  'Rural Bank of Cavinti (Laguna), Inc.',
  'Rural Bank of Central Pangasinan (Bayambang), Inc.',
  'Rural Bank of Compostela (COMVAL), Inc.',
  'Rural Bank of Cuartero (Capiz), Inc.',
  'Rural Bank of Cuenca, Inc.',
  'Rural Bank of Digos, Inc.',
  'Rural Bank of Dolores (Quezon), Inc.',
  'Rural Bank of Doña Remedios Trinidad (Bulacan), Inc.',
  'Rural Bank of Donsol (Sorsogon), Inc.',
  'Rural Bank of Dulag (Leyte), Inc.',
  'Rural Bank of Dumangas, Inc.',
  'Rural Bank of Dupax (Nueva Vizcaya), Inc.',
  'Rural Bank of El Salvador (Misamis Oriental), Inc.',
  'Rural Bank of Escalante (Negros Occ.), Inc.',
  'Rural Bank of Gandara (Samar), Inc.',
  'Rural Bank of Gattaran (Cagayan), Inc.',
  'Rural Bank of General Luna (Quezon), Inc.',
  'Rural Bank of Gingoog, Inc.',
  'Rural Bank of Gitagum (Misamis Oriental), Inc.',
  'Rural Bank of Gloria (Or. Min.), Inc.',
  'Rural Bank of Guinobatan, Inc.',
  'Rural Bank of Guiuan (Eastern Samar), Inc.',
  'Rural Bank of Hermosa (Bataan), Inc.',
  'Rural Bank of Hilongos,Inc.',
  'Rural Bank of Hindang (Leyte), Inc.',
  'Rural Bank of Hinundayan (Southern Leyte), Inc.',
  'Rural Bank of Ilog (Negros Occidental), Inc.',
  'Rural Bank of Iloilo City, Inc.',
  'Rural Bank of Infanta, Inc.',
  'Rural Bank of Irosin, Inc.',
  'Rural Bank of Itogon (Benguet), Inc.',
  'Rural Bank of Jaen, Inc.',
  'Rural Bank of Jamindan (Capiz), Inc.',
  'Rural Bank of Jimenez (Misamis Occidental), Inc.',
  'Rural Bank of Jose Panganiban (Camarines Norte), Inc.',
  'Rural Bank of Kapalong (Davao), Inc.',
  'Rural Bank of Kiamba (Sarangani), Inc.',
  'Rural Bank of Kibawe (Bukidnon), Inc.',
  'Rural Bank of La Paz, Inc.',
  'Rural Bank of Labason (Zamboanga Del Norte), Incorporated',
  'Rural Bank of Lanuza (Surigao Del Sur), Inc.',
  'Rural Bank of Lebak (Sultan Kudarat), Incorporated',
  'Rural Bank of Liloy (Zamboanga Del Norte), Inc.',
  'Rural Bank of Lipa City (Batangas), Inc.',
  'Rural Bank of Loon (Bohol), Inc.',
  'Rural Bank of Lopez Jaena (Misamis Occidental), Inc.',
  'Rural Bank of Lucban (Quezon), Inc.',
  'Rural Bank of Luisiana (Laguna), Inc.',
  'Rural Bank of Lumban (Laguna), Inc.',
  'Rural Bank of Mabalacat, Inc.',
  'Rural Bank of Madridejos (Cebu), Inc.',
  'Rural Bank of Magdalena (Laguna), Inc.',
  'Rural Bank of Makato (Aklan), Inc.',
  'Rural Bank of Malitbog (Southern Leyte), Inc.',
  'Rural Bank of Malolos, Inc.',
  'Rural Bank of Mambusao, Inc.',
  'Rural Bank of Manapla (Neg. Occ.), Inc.',
  'Rural Bank of Mangaldan, Inc. (Bangko Rural Ng Mangaldan, Ink.)',
  'Rural Bank of Manolo Fortich, Bukidnon, Inc.',
  'Rural Bank of Manukan (Zamboanga Del Norte), Inc.',
  'Rural Bank of Maria Aurora (Aurora), Inc.',
  'Rural Bank of Marilag (Sta. Maria, Laguna), Inc.',
  'Rural Bank of Mati, Inc.',
  'Rural Bank of Mauban, Inc.',
  'Rural Bank of Medina (Misamis Oriental), Inc.',
  'Rural Bank of Miagao (Iloilo), Inc.',
  'Rural Bank of Midsayap, Inc.',
  'Rural Bank of Montalban, Inc.',
  'Rural Bank of Naic, Inc.',
  'Rural Bank of New Washington (Aklan), Inc.',
  'Rural Bank of Norala (S. Cotabato), Inc.',
  'Rural Bank of Padre Garcia, Inc.',
  'Rural Bank of Paete, Inc.',
  'Rural Bank of Pagadian, Inc.',
  'Rural Bank of Pamplona (Negros Oriental), Inc.',
  'Rural Bank of Paracale (Camarines Norte), Incorporated',
  'Rural Bank of Pilar (Bataan), Inc.',
  'Rural Bank of Pilar (Sorsogon), Inc.',
  'Rural Bank of Pinamalayan, Inc.',
  'Rural Bank of Placer, (Surigao del Norte), Inc.',
  'Rural Bank of Pola (Oriental Mindoro), Inc.',
  'Rural Bank of Porac (Pampanga), Inc.',
  'Rural Bank of Puerto Galera (Oriental Mindoro), Inc.',
  'Rural Bank of Pura (Tarlac), Inc.',
  'Rural Bank of Quezon (N. E.), Inc.',
  'Rural Bank of Ramon (Isabela), Inc.',
  'Rural Bank of Rizal (Kalinga-Apayao), Inc.',
  'Rural Bank of Rizal (Laguna), Inc.',
  'Rural Bank of Rizal (Zamboanga Del Norte), Inc.',
  'Rural Bank of Rosario (La Union), Inc.',
  'Rural Bank of Roxas (Oriental Mindoro), Inc.',
  'Rural Bank of Sagada (Mountain Province), Inc.',
  'Rural Bank of Sagay, Inc.',
  'Rural Bank of Salug (Zamboanga Del Norte), Inc.',
  'Rural Bank of Sampaloc (Quezon), Inc.',
  'Rural Bank of San Antonio, Inc.',
  'Rural Bank of San Luis (Batangas), Inc.',
  'Rural Bank of San Luis (Pampanga), Inc.',
  'Rural Bank of San Manuel (Isabela), Inc.',
  'Rural Bank of San Mateo (Isabela), Inc.',
  'Rural Bank of San Miguel (Iloilo), Inc.',
  'Rural Bank of San Narciso, Inc.',
  'Rural Bank of San Pascual (Obando, Bulacan), Inc.',
  'Rural Bank of San Rafael (Bulacan), Inc.',
  'Rural Bank of Sanchez Mira (Cagayan), Inc.',
  'Rural Bank of Sasmuan (Pampanga), Incorporated',
  'Rural Bank of Seven Lakes (San Pablo City), Inc.',
  'Rural Bank of Silay City, Inc.',
  'Rural Bank of Sipocot (Camarines Sur), Inc.',
  'Rural Bank of Socorro (Oriental Mindoro), Inc.',
  'Rural Bank of Solano (Nueva Vizcaya), Inc.',
  'Rural Bank of Sta. Barbara (Iloilo), Inc.',
  'Rural Bank of Sta. Ignacia, Inc.',
  'Rural Bank of Sta. Maria (Ilocos Sur), Inc.',
  'Rural Bank of Sta. Rosa De Lima (Paniqui, Tarlac), Inc.',
  'Rural Bank of Sudipen (La Union), Inc.',
  'Rural Bank of Taal, Inc.',
  'Rural Bank of Talugtug (N. E.), Inc.',
  'Rural Bank of Tandag (Surigao Del Sur), Inc.',
  'Rural Bank of Tangub City, (Mis. Occ.) Inc.',
  'Rural Bank of Tanjay, Inc.',
  'Rural Bank of Tigaon Camarines Sur, Inc.',
  'Rural Bank of Tigbauan (Iloilo), Inc.',
  'Rural Bank of Victoria, Inc.',
  'Rural Bank of Villaverde (Nueva Vizcaya), Inc.',
  'Rural Bank of Zarraga (Iloilo), Inc.',
  'Sadiri Rural Bank (San Juan, Ilocos Sur), Inc.',
  'Salmon Bank (Rural Bank) Inc.',
  'Salug Valley Rural Bank, Inc.',
  'San Bartolome Rural Bank, Inc.',
  'Saviour Rural Bank, Inc.',
  'Second Rural Bank of San Luis, Pampanga, Inc.',
  'Second Rural Bank of Valenzuela (Metro Manila), Inc.',
  'Secured Bank, Inc., A Rural Bank',
  'Security Bank Corporation',
  'SG Bank - A Rural bank Inc.',
  'Shinhan Bank - Manila Branch',
  'Silahis Bank, Inc. (A Rural Bank)',
  'South Bank (A Rural Bank), Incorporated',
  'Southeast Country Bank, Inc. ( A Rural Bank)',
  'St. Michael Rural Bank of Tarlac (Tarlac), Inc.',
  'Sta. Maria Rural Bank, Inc.',
  'Standard Chartered Bank',
  'Sterling Bank of Asia, Inc. (A Savings Bank)',
  'Sumitomo Mitsui Banking Corporation-Manila Branch',
  'Summit Bank (Rural Bank of Tublay, Inc.)',
  'Summit Rural Bank of Lipa City, Inc.',
  'Sun Savings Bank, Inc.',
  'Sunrise Rural Bank (Rosario, Batangas), Inc.',
  'Talisay Rural Bank, Inc.',
  'Tamaraw Rural Bank, Inc.',
  'The Country Bank Rural Bank of Bongabong Or. Mindoro, Inc.',
  'The Hongkong & Shanghai Banking Corporation',
  'Tonik Digital Bank, Inc.',
  'Top Bank Philippines, Inc. (A Rural Bank)',
  'Towncall Rural Bank, Inc.',
  'TRBank Inc. (A Rural Bank)',
  'Turumba Rural Bank of Pakil, Inc.',
  'UCPB Savings Bank',
  'Unilink Bank, Inc. (A Rural Bank)',
  'Union Bank of the Philippines',
  'UnionDigital Bank, Inc.',
  'United Overseas Bank Limited, Manila Branch',
  'Universal Rural Bank of Lopez (Quezon), Inc.',
  'University Savings Bank, Inc.',
  'UNObank, Inc.',
  'Upland Rural Bank of Dalaguete (Cebu), Inc.',
  'Utility Bank, Inc. (A Rural Bank)',
  'Vigan Banco Rural Incorporada',
  'Village Bank, Inc. (A Rural Bank)',
  'Vizcaya Bank, A Rural Bank, Inc.',
  'Wealth Development Bank Corporation',
  'Welcome Bank (Rural Bank), Inc.',
  'Yuanta Savings Bank Philippines, Inc.',
  'Zambales Rural Bank, Inc.',
  'Zamboanga City Rural Bank, Inc.',
  'Zamboanga Del Norte Cooperative Bank',
];

function getLocalTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getLocalCurrentMonth() {
  return getLocalTodayIsoDate().slice(0, 7);
}

function getEmptyForm() {
  return {
  type: 'received',
  date: getLocalTodayIsoDate(),
  supplier: '',
  quantity: '',
  netKg: '',
  lansa: '',
  caAmount: '',
  bank: '',
  proofPreview: '',
  removeProofImage: false,
  };
}

function DailyEntriesPage({ navigationItems, activePath, onNavigate }) {
  const deleteConfirmation = useSaveConfirmation();
  const saveConfirmation = useSaveConfirmation();
  const proofBrowseScrollTopRef = useRef(0);
  const [entries, setEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lansas, setLansas] = useState([]);
  const [form, setForm] = useState(getEmptyForm);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterMonth, setFilterMonth] = useState(getLocalCurrentMonth);
  const [currentPage, setCurrentPage] = useState(1);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isEntryFormExpanded, setIsEntryFormExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [proofPreviewModal, setProofPreviewModal] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadDailyEntries() {
      setIsLoading(true);
      setFormError('');

      try {
        const [loadedEntries, loadedSuppliers, loadedLansas] = await Promise.all([
          fetchDailyEntries(),
          fetchSuppliers(),
          fetchLansas(),
        ]);

        if (isMounted) {
          setEntries(loadedEntries);
          setSuppliers(loadedSuppliers);
          setLansas(loadedLansas);
        }
      } catch (error) {
        if (isMounted) {
          setFormError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadDailyEntries();

    return () => {
      isMounted = false;
    };
  }, []);

  const effectiveFilterDate = filterDate || (!filterMonth ? getLocalTodayIsoDate() : '');
  const effectiveFilterMonth = filterMonth || (!filterDate ? getLocalCurrentMonth() : '');
  const filteredEntries = useMemo(
    () =>
      entries.filter((entry) => {
        const matchesType = filterType === 'all' || entry.type === filterType;
        const matchesDate = !effectiveFilterDate || entry.date === effectiveFilterDate;
        const matchesMonth =
          !effectiveFilterMonth || String(entry.date || '').startsWith(effectiveFilterMonth);

        return matchesType && matchesDate && matchesMonth;
      }),
    [effectiveFilterDate, effectiveFilterMonth, entries, filterType]
  );
  const totalFilteredEntries = filteredEntries.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredEntries / DAILY_ENTRIES_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * DAILY_ENTRIES_PAGE_SIZE;
  const endIndex = Math.min(startIndex + DAILY_ENTRIES_PAGE_SIZE, totalFilteredEntries);
  const paginatedEntries = useMemo(
    () => filteredEntries.slice(startIndex, endIndex),
    [endIndex, filteredEntries, startIndex]
  );
  const entriesPagination = {
    currentPage: normalizedPage,
    endItem: endIndex,
    isNextDisabled: normalizedPage >= totalPages,
    isPreviousDisabled: normalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, totalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: totalFilteredEntries ? startIndex + 1 : 0,
    totalItems: totalFilteredEntries,
    totalPages,
  };
  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((supplier) => String(supplier.status || '').toLowerCase() === 'active')
        .map((supplier) => ({
          value: supplier.companyName,
          label: supplier.companyName,
        }))
        .filter((supplierOption) => String(supplierOption.value || '').trim())
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label)),
    [suppliers]
  );
  const lansaOptions = useMemo(
    () =>
      lansas
        .map((lansa) => ({
          value: lansa.name,
          label: lansa.name,
        }))
        .filter((lansaOption) => String(lansaOption.value || '').trim())
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label)),
    [lansas]
  );
  const todayLabel = useMemo(() => formatLongLocalDate(getLocalTodayIsoDate()), []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterDate, filterMonth, filterType]);

  function updateFormValue(name, value) {
    const nextValue = formatDailyEntryFieldValue(name, value);

    if (nextValue === null) {
      return;
    }

    setFormError('');
    setFieldErrors((current) => ({
      ...current,
      [name]: '',
    }));
    setForm((current) => ({
      ...current,
      [name]: nextValue,
    }));
  }

  function selectEntryType(type) {
    setFormError('');
    setFieldErrors({});
    setForm((current) => ({
      ...current,
      type,
      lansa: type === 'cash' ? '' : current.lansa,
    }));
  }

  function updateFilterDate(value) {
    setFilterDate(value);
    setFilterMonth(value ? value.slice(0, 7) : '');
  }

  function updateFilterMonth(value) {
    setFilterMonth(value);
    setFilterDate('');
  }

  function getAppScrollContainer() {
    return document.querySelector('[data-app-scroll-container="true"]');
  }

  function rememberProofBrowseScrollPosition() {
    const scrollContainer = getAppScrollContainer();
    proofBrowseScrollTopRef.current = scrollContainer?.scrollTop || 0;
  }

  function restoreProofBrowseScrollPosition() {
    const scrollContainer = getAppScrollContainer();

    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTop = proofBrowseScrollTopRef.current;
  }

  async function handleProofChange(event) {
    const inputElement = event.target;
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setFormError('Please upload a JPG or PNG image.');
      setFieldErrors({});
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be 5MB or smaller.');
      setFieldErrors({});
      return;
    }

    try {
      const convertedImage = await convertImageFileToWebpDataUrl(file, {
        maxBytes: 5 * 1024 * 1024,
        quality: 0.85,
      });

      setForm((current) => ({
        ...current,
        proofPreview: convertedImage.dataUrl,
        removeProofImage: false,
      }));
      setFormError('');
      setFieldErrors((current) => ({
        ...current,
        proofPreview: '',
      }));
      inputElement.value = '';
      window.requestAnimationFrame(() => {
        restoreProofBrowseScrollPosition();
      });
    } catch (error) {
      setFormError(error.message || 'Unable to convert proof image to WEBP.');
      setFieldErrors({});
      inputElement.value = '';
    }
  }

  function clearProofImage() {
    setFormError('');
    setFieldErrors((current) => ({
      ...current,
      proofPreview: '',
    }));
    setForm((current) => ({
      ...current,
      proofPreview: '',
      removeProofImage: true,
    }));
  }

  function openProofPreview(src, title, retention = null) {
    if (!src) {
      return;
    }

    setProofPreviewModal({ src, title, retention });
  }

  function closeProofPreview() {
    setProofPreviewModal(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationErrors = validateForm(form);

    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setFormError('');
      return;
    }

    setFieldErrors({});
    setFormError('');
    saveConfirmation.requestConfirmation(event, handleConfirmedSubmit, getEntrySaveConfirmation(form, editingId));
  }

  async function handleConfirmedSubmit(event) {
    event?.preventDefault?.();
    const validationErrors = validateForm(form);

    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setFormError('');
      return;
    }

    setIsSaving(true);
    setFormError('');
    setFieldErrors({});

    try {
      const payload = buildDailyEntryPayload(form);
      const savedEntry = editingId
        ? await updateDailyEntry(editingId, payload)
        : await createDailyEntry(payload);

      setEntries((current) =>
        editingId
          ? current.map((entry) => (entry.id === editingId ? savedEntry : entry))
          : [savedEntry, ...current]
      );
      clearForm();
      setIsEntryFormExpanded(false);
    } catch (error) {
      setFieldErrors({});
      setFormError(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  function clearForm(options = {}) {
    setForm(() => ({
      ...getEmptyForm(),
      ...(options.preserveType ? { type: form.type } : {}),
    }));
    setEditingId(null);
    setFormError('');
    setFieldErrors({});
  }

  function editEntry(entry) {
    setEditingId(entry.id);
    setForm({
      type: entry.type,
      date: entry.date,
      supplier: entry.supplier,
      quantity: entry.quantity || '',
      netKg: formatCommaSeparatedDecimalValue(entry.netKg || '', 2),
      lansa: entry.lansa === '-' ? '' : entry.lansa || '',
      caAmount: formatCommaSeparatedDecimalValue(entry.caAmount || '', 2),
      bank: entry.bank || '',
      proofPreview: entry.isProofExpired ? '' : entry.proofPreview || '',
      removeProofImage: false,
    });
    setFormError('');
    setFieldErrors({});
    setIsEntryFormExpanded(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function requestDeleteEntry(entry) {
    deleteConfirmation.requestConfirmation(null, () => deleteEntry(entry.id), {
      title: 'Delete daily entry?',
      message: `This will permanently delete the ${entry.type === 'cash' ? 'cash advance' : 'received stock'} entry for ${entry.supplier}.`,
      confirmLabel: 'Delete Entry',
      confirmingLabel: 'Deleting...',
      cancelLabel: 'Keep Entry',
    });
  }

  async function deleteEntry(entryId) {
    setFormError('');

    try {
      await deleteDailyEntry(entryId);
      setEntries((current) => current.filter((entry) => entry.id !== entryId));

      if (editingId === entryId) {
        clearForm();
      }
    } catch (error) {
      setFormError(error.message);
    }
  }

  return (
    <AppShell navigationItems={navigationItems} activePath={activePath} onNavigate={onNavigate}>
      <div className="bg-white px-4 py-5 text-[#17213a] sm:px-6 lg:px-[26px]">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3 mt-1">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-[#d9e3f0] bg-white text-[#31446b] shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
              <CalendarDays className="h-7 w-7" aria-hidden="true" />
            </div>
            <div>
              <h1 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[#0f1833] sm:text-[1.4rem]">
                Daily Seaweed Entries
              </h1>
              <p className="m-0 mt-0 text-[0.96rem] leading-6 text-[#5d6782]">
                Track received seaweed stocks and cash advances daily.
              </p>
            </div>
          </div>

          <div className="flex min-w-[210px] items-center gap-3 rounded-xl border border-[#d9e3f0] bg-white px-4 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
            <CalendarDays className="h-6 w-6 text-[#31446b]" aria-hidden="true" />
            <div>
              <p className="m-0 text-[0.72rem] font-medium text-[#697594]">Today</p>
              <p className="m-0 text-[1rem] font-semibold text-[#17213a]">{todayLabel}</p>
            </div>
          </div>
        </header>

        <form
          className="mt-3 rounded-xl border border-[#d7dfeb] bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] sm:p-5"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="m-0 text-[1.18rem] font-semibold tracking-[-0.02em] text-[#17213a]">
                {editingId ? 'Edit Entry' : 'Add New Entry'}
              </h2>
              {formError ? (
                <p className="m-0 mt-1 text-sm font-semibold text-[#e11d48]">{formError}</p>
              ) : (
                <p className="m-0 mt-1 text-sm text-[#697594]">
                  {isEntryFormExpanded
                    ? 'Fill in a daily received stock or cash advance entry.'
                    : 'Expand only when you need to add or update an entry.'}
                </p>
              )}
            </div>
            <button
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-4 text-[0.88rem] font-semibold text-white shadow-[0_12px_24px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:border-[var(--theme-accent-strong)] hover:bg-[var(--theme-accent-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2"
              type="button"
              aria-expanded={isEntryFormExpanded}
              onClick={() => setIsEntryFormExpanded((current) => !current)}
            >
              {isEntryFormExpanded ? (
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              )}
              {isEntryFormExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>

          {isEntryFormExpanded ? (
            <>
              <div className="mt-4 grid w-full grid-cols-2 gap-2 rounded-xl border border-[var(--theme-border-soft)] bg-[var(--theme-accent-soft)] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] sm:inline-grid sm:w-auto sm:grid-cols-2">
                <EntryTypeTab
                  icon={Truck}
                  label="Received Stock"
                  description="Stocks received"
                  tone="received"
                  selected={form.type === 'received'}
                  onClick={() => selectEntryType('received')}
                />
                <EntryTypeTab
                  icon={CircleDollarSign}
                  label="Cash Advance (CA)"
                  description="CA request"
                  tone="cash"
                  selected={form.type === 'cash'}
                  onClick={() => selectEntryType('cash')}
                />
              </div>

              <div className="mt-4">
                {form.type === 'received' ? (
                  <DetailsPanel
                    title="Received Stock Details"
                    tone="received"
                    active
                    fields={[
                      { icon: CalendarDays, name: 'date', label: 'Date', type: 'date' },
                      {
                        icon: User,
                        name: 'supplier',
                        label: 'From (Supplier)',
                        placeholder: 'Select supplier',
                        type: 'supplierSelect',
                      },
                      {
                        icon: ShoppingCart,
                        name: 'quantity',
                        label: 'Quantity (Sacks)',
                        placeholder: 'e.g. 150',
                        inputMode: 'numeric',
                      },
                      {
                        icon: Scale,
                        name: 'netKg',
                        label: 'Net KG (With Sacks Weight)',
                        placeholder: 'e.g. 11682.50',
                        inputMode: 'decimal',
                      },
                      {
                        icon: Ship,
                        name: 'lansa',
                        label: 'Lansa (Motor Launch / Cargo Boat)',
                        placeholder: 'Select lansa (optional)',
                        type: 'lansaSelect',
                      },
                    ]}
                    form={form}
                    errors={fieldErrors}
                    supplierOptions={supplierOptions}
                    lansaOptions={lansaOptions}
                    uploadTitle="Upload Proof of Stocks Received (Image) (optional)"
                    proofType="stock"
                    onBeforeProofBrowse={rememberProofBrowseScrollPosition}
                    onAfterProofBrowse={restoreProofBrowseScrollPosition}
                    onFieldChange={updateFormValue}
                    onProofChange={handleProofChange}
                    onProofClear={clearProofImage}
                    onProofPreview={openProofPreview}
                  />
                ) : (
                  <DetailsPanel
                    title="Cash Advance Details"
                    tone="cash"
                    active
                    fields={[
                      { icon: CalendarDays, name: 'date', label: 'Date', type: 'date' },
                      {
                        icon: User,
                        name: 'supplier',
                        label: 'Supplier / Requested By',
                        placeholder: 'Select supplier',
                        type: 'supplierSelect',
                      },
                      {
                        icon: Banknote,
                        name: 'caAmount',
                        label: 'CA Amount (P)',
                        placeholder: 'e.g. 300,000',
                        inputMode: 'decimal',
                      },
                      {
                        icon: Building2,
                        name: 'bank',
                        label: 'Bank',
                        placeholder: 'e.g. METROBANK',
                        suggestions: BANK_SUGGESTIONS,
                      },
                    ]}
                    form={form}
                    errors={fieldErrors}
                    supplierOptions={supplierOptions}
                    lansaOptions={lansaOptions}
                    uploadTitle="Upload Proof of Bank Note / Deposit Slip (Image) (optional)"
                    proofType="bank"
                    onBeforeProofBrowse={rememberProofBrowseScrollPosition}
                    onAfterProofBrowse={restoreProofBrowseScrollPosition}
                    onFieldChange={updateFormValue}
                    onProofChange={handleProofChange}
                    onProofClear={clearProofImage}
                    onProofPreview={openProofPreview}
                  />
                )}
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[var(--theme-accent)] px-8 text-[0.94rem] font-semibold text-white shadow-[0_14px_26px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
                  type="submit"
                  disabled={isSaving}
                >
                  {editingId ? <Save className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
                  {isSaving ? 'Saving...' : editingId ? 'Save Entry' : 'Add Entry'}
                </button>
                <button
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-[var(--theme-border)] bg-white px-8 text-[0.94rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.04)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2"
                  type="button"
                  onClick={() => clearForm({ preserveType: true })}
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Clear
                </button>
              </div>
            </>
          ) : null}
        </form>

        <section className="mt-4 rounded-xl border border-[#d7dfeb] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="flex flex-col gap-4 px-4 py-3 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
                <Table2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="m-0 text-[1.12rem] font-semibold tracking-[-0.02em] text-[#17213a]">
                Daily Entries List
              </h2>
              <span
                className={`rounded-full px-3 py-1 text-[0.78rem] font-semibold ${
                  filteredEntries.length
                    ? 'bg-[#ecfdf3] text-[#15803d]'
                    : 'bg-[#fff1f2] text-[#e11d48]'
                }`}
              >
                <span>{filteredEntries.length}</span> entries
              </span>
            </div>

            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-[180px_180px_180px]">
              <FilterSelect
                label="Filter by Type"
                value={filterType}
                onChange={setFilterType}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'received', label: 'Received Stock' },
                  { value: 'cash', label: 'Cash Advance' },
                ]}
              />
              <MonthFilter value={filterMonth} onChange={updateFilterMonth} />
              <DateFilter value={filterDate} focusMonth={filterMonth} onChange={updateFilterDate} />
            </div>
          </div>

          <div className="mx-4 sm:mx-5 relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
            <div className="overflow-x-auto">
              <table className="min-w-[1120px] w-full border-collapse text-left">
                <thead>
                  <tr className="bg-[var(--theme-accent-panel)]">
                    {[
                      { label: '#', className: 'text-center' },
                      { label: 'Date' },
                      { label: 'Type / From (Supplier)' },
                      { label: 'Qty / CA Amount', className: 'text-center' },
                      { label: 'Net KG / Bank', className: 'text-center' },
                      { label: 'Lansa', className: 'text-center' },
                      { label: 'Proof', className: 'text-center' },
                      { label: 'Actions', className: 'text-center' },
                    ].map((column) => (
                      <DailyEntriesTableHeading
                        key={column.label}
                        className={column.className}
                      >
                        {column.label}
                      </DailyEntriesTableHeading>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedEntries.map((entry, index) => (
                    <EntryRow
                      entry={entry}
                      index={startIndex + index + 1}
                      key={entry.id}
                      onEdit={editEntry}
                      onDelete={requestDeleteEntry}
                      onPreviewProof={openProofPreview}
                    />
                  ))}
                  {!filteredEntries.length ? (
                    <tr className="border-t border-[var(--theme-border-soft)]">
                      <td className="px-4 py-12 text-center text-[var(--theme-text-muted)]" colSpan={8}>
                        {isLoading ? 'Loading daily entries...' : 'No entries match the selected filters.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {entriesPagination.totalItems > DAILY_ENTRIES_PAGE_SIZE ? (
            <PaginationFooter {...entriesPagination} />
          ) : null}

          <div className="grid gap-3 bg-white px-4 py-3 lg:grid-cols-2">
            <div className="flex flex-wrap items-center gap-5 rounded-lg border border-[#e4e9f2] bg-[#fbfcff] px-4 py-3 text-[0.82rem] text-[#5d6782]">
              <span className="font-semibold text-[#31446b]">Legend:</span>
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 rounded bg-[#22c6d2]" />
                Received Stock (Seaweed)
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 rounded bg-[#ff8f9e]" />
                Cash Advance (CA)
              </span>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-[var(--theme-border-soft)] bg-[var(--theme-accent-soft)] px-4 py-3 text-[0.82rem] text-[var(--theme-text-strong)]">
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--theme-accent)] text-[0.72rem] font-bold text-white">
                i
              </span>
              <span>
                <strong>Note:</strong> Lansa (Motor Launch / Cargo Boat) is optional for Received
                Stock and Cash Advance entries.
              </span>
            </div>
          </div>
        </section>
        {proofPreviewModal ? (
          <ProofPreviewModal
            src={proofPreviewModal.src}
            title={proofPreviewModal.title}
            retention={proofPreviewModal.retention}
            onClose={closeProofPreview}
          />
        ) : null}
        <SaveConfirmationModal {...saveConfirmation.modalProps} />
        <SaveConfirmationModal {...deleteConfirmation.modalProps} />
      </div>
    </AppShell>
  );
}

function ProofPreviewModal({ src, title, retention, onClose }) {
  const [now, setNow] = useState(() => new Date());
  const proofRetention = getProofImageRetention(retention, now);

  useEffect(() => {
    if (!retention?.expiresAt) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [retention?.expiresAt]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(15,23,42,0.72)]"
      className="overflow-hidden"
      contentClassName="items-center px-2 py-3 sm:px-6 sm:py-6"
      zIndexClassName="z-[80]"
    >
      <div
        className="relative grid max-h-[calc(100vh-1.5rem)] gap-3 overflow-hidden rounded-xl border border-white/18 bg-white p-3 shadow-[0_24px_70px_rgba(15,23,42,0.36)] sm:max-h-[calc(100vh-3rem)] sm:p-4"
        style={{ width: 'min(100%, calc(100vw - 1rem))', maxWidth: '64rem' }}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Proof image preview'}
      >
        <div className="min-w-0 pr-11">
          <div className="grid min-w-0 gap-2 sm:flex sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0">
            <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[#697594]">
              Image Preview
            </p>
            <h3 className="m-0 line-clamp-2 break-words text-[0.9rem] font-semibold leading-5 text-[#17213a] sm:text-[1rem]">
              {title || 'Proof image'}
            </h3>
            </div>
            {proofRetention.label ? (
              <span
                className={`w-max max-w-full rounded-full border px-3 py-1.5 text-[0.74rem] font-semibold shadow-[0_8px_22px_rgba(15,23,42,0.12)] ${
                  proofRetention.tone === 'danger'
                    ? 'border-[#fecdd3] bg-[#fff1f2] text-[#e11d48]'
                    : proofRetention.tone === 'warning'
                      ? 'border-[#f8dea1] bg-[#fff8e6] text-[#8a6424]'
                      : 'border-[#c9e6d0] bg-[#f4fff6] text-[#2f7a44]'
                }`}
              >
                {proofRetention.modalLabel || proofRetention.label}
              </span>
            ) : null}
          </div>
        </div>
        <button
          className="absolute right-3 top-3 z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#d8e1ee] bg-white text-[#31446b] shadow-[0_8px_18px_rgba(15,23,42,0.12)] transition hover:bg-[#f8fbff] sm:h-9 sm:w-9"
          type="button"
          aria-label="Close image preview"
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="h-[calc(100vh-10.5rem)] min-h-0 max-h-[70vh] overflow-auto rounded-lg bg-[#0f172a] sm:h-auto sm:max-h-[78vh]">
          <img
            className="block h-auto w-full"
            src={src}
            alt={title || 'Proof preview'}
          />
        </div>
      </div>
    </ModalBackdrop>
  );
}

function EntryTypeTab({ icon: Icon, label, description, tone, selected = false, onClick }) {
  return (
    <button
      className={`relative grid min-h-[86px] min-w-0 grid-cols-[34px_minmax(0,1fr)] items-center gap-2 rounded-lg px-2.5 py-2 text-left transition sm:min-h-[58px] sm:min-w-[235px] sm:grid-cols-[36px_minmax(0,1fr)] sm:gap-3 sm:px-4 ${
        selected
          ? 'bg-white text-[var(--theme-accent)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.1)] ring-1 ring-[var(--theme-accent)]'
          : 'text-[var(--theme-accent)] hover:bg-white/70'
      }`}
      type="button"
      onClick={onClick}
    >
      <span
        className={`grid h-8 w-8 shrink-0 place-items-center rounded-full sm:h-9 sm:w-9 ${
          selected ? 'bg-[var(--theme-accent-soft)]' : 'bg-white/85'
        }`}
      >
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block break-words text-[0.86rem] font-semibold leading-5 sm:text-[0.94rem]">{label}</span>
        <span className="mt-0.5 block text-[0.76rem] text-[#5d6782]">{description}</span>
      </span>
    </button>
  );
}

function DetailsPanel({
  title,
  fields,
  uploadTitle,
  tone,
  proofType,
  active,
  form,
  errors = {},
  supplierOptions = [],
  lansaOptions = [],
  onBeforeProofBrowse,
  onAfterProofBrowse,
  onFieldChange,
  onProofChange,
  onProofClear,
  onProofPreview,
}) {
  const isCash = tone === 'cash';

  return (
    <div
      className={`rounded-lg border p-3.5 transition ${
        isCash
          ? 'border-[#ffc7cf] bg-[linear-gradient(135deg,#fffafa,#fff5f6)]'
          : 'border-[#a8e4ee] bg-[linear-gradient(135deg,#f5fdff,#ffffff)]'
      } ${active ? '' : 'opacity-55'}`}
    >
      <h3
        className={`m-0 flex items-center gap-2 text-[0.84rem] font-semibold uppercase tracking-[0.045em] ${
          isCash ? 'text-[#ff2439]' : 'text-[#007a92]'
        }`}
      >
        {title}
        <span
          className={`grid h-5 w-5 place-items-center rounded-full border text-[0.68rem] font-bold leading-none shadow-[0_4px_10px_rgba(15,23,42,0.08)] ${
            isCash
              ? 'border-[#ff8f9e] bg-[#fff1f3] text-[#d9182f]'
              : 'border-[#22c6d2] bg-[#effdff] text-[#007a92]'
          }`}
          title={`${title} section`}
          aria-label={`${title} section`}
        >
          !
        </span>
      </h3>

      <div className="mt-3 grid items-start gap-3 md:grid-cols-3">
        {fields.map((field) => (
          <EntryField
            key={`${tone}-${field.name}`}
            {...field}
            disabled={!active}
            value={form[field.name] || ''}
            error={errors[field.name]}
            options={
              field.type === 'supplierSelect'
                ? supplierOptions
                : field.type === 'lansaSelect'
                  ? lansaOptions
                  : []
            }
            onChange={(value) => onFieldChange(field.name, value)}
          />
        ))}
      </div>

      <UploadBox
        title={uploadTitle}
        tone={tone}
        proofType={proofType}
        disabled={!active}
        preview={active ? form.proofPreview : ''}
        onBeforeProofBrowse={onBeforeProofBrowse}
        onAfterProofBrowse={onAfterProofBrowse}
        onProofChange={onProofChange}
        onProofClear={onProofClear}
        onProofPreview={onProofPreview}
      />
    </div>
  );
}

function EntryField({
  icon: Icon,
  label,
  placeholder,
  wide = false,
  value,
  error = '',
  options = [],
  suggestions = [],
  onChange,
  type = 'text',
  inputMode,
  disabled,
}) {
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);

  if (type === 'date') {
    return (
      <div className={`grid content-start gap-1.5 ${wide ? 'md:col-span-2' : ''}`}>
        <CustomDatePicker
          label={label}
          name="date"
          value={value}
          error={error}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          preferredPlacement="auto"
          leadingIcon={Icon}
          leadingIconClassName={`h-4 w-4 shrink-0 ${error ? 'text-[#e11d48]' : 'text-[#667391]'}`}
          errorClassName="text-[0.74rem] font-semibold text-[#e11d48]"
          labelClassName="text-[0.82rem] font-semibold text-[#17213a]"
          triggerClassName="min-h-[42px] px-3 py-0 text-[0.9rem] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]"
          triggerShapeClassName="rounded-md"
        />
      </div>
    );
  }

  if (type === 'supplierSelect') {
    const normalizedValue = String(value || '');
    const hasSelectedOption = options.some((option) => option.value === normalizedValue);
    const selectOptions =
      normalizedValue && !hasSelectedOption
        ? [{ value: normalizedValue, label: normalizedValue }, ...options]
        : options;

    return (
      <div className={`grid content-start gap-1.5 ${wide ? 'md:col-span-2' : ''}`}>
        <CustomSelect
          label={label}
          name="supplier"
          value={normalizedValue}
          error={error}
          onChange={(event) => onChange(event.target.value)}
          options={selectOptions}
          placeholder={selectOptions.length ? placeholder : 'No active suppliers'}
          disabled={disabled || !selectOptions.length}
          leadingIcon={Icon}
          leadingIconClassName={`h-4 w-4 shrink-0 ${error ? 'text-[#e11d48]' : 'text-[#667391]'}`}
          errorClassName="text-[0.74rem] font-semibold text-[#e11d48]"
          labelClassName="text-[0.82rem] font-semibold text-[#17213a]"
          triggerClassName="min-h-[42px] px-3 py-0 text-[0.9rem] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]"
          triggerShapeClassName="rounded-md"
        />
      </div>
    );
  }

  if (type === 'lansaSelect') {
    const normalizedValue = String(value || '');
    const hasSelectedOption = options.some((option) => option.value === normalizedValue);
    const selectOptions =
      normalizedValue && !hasSelectedOption
        ? [{ value: normalizedValue, label: normalizedValue }, ...options]
        : options;

    return (
      <div className={`grid content-start gap-1.5 ${wide ? 'md:col-span-2' : ''}`}>
        <CustomSelect
          label={label}
          name="lansa"
          value={normalizedValue}
          error={error}
          onChange={(event) => onChange(event.target.value)}
          options={selectOptions}
          placeholder={selectOptions.length ? placeholder : 'No lansa names found'}
          clearOptionLabel="None"
          disabled={disabled}
          leadingIcon={Icon}
          leadingIconClassName={`h-4 w-4 shrink-0 ${error ? 'text-[#e11d48]' : 'text-[#667391]'}`}
          errorClassName="text-[0.74rem] font-semibold text-[#e11d48]"
          labelClassName="text-[0.82rem] font-semibold text-[#17213a]"
          triggerClassName="min-h-[42px] px-3 py-0 text-[0.9rem] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]"
          triggerShapeClassName="rounded-md"
        />
      </div>
    );
  }

  const normalizedSearchValue = String(value || '').trim().toLowerCase();
  const filteredSuggestions = suggestions.filter((suggestion) => {
    const normalizedSuggestion = String(suggestion || '').toLowerCase();

    return normalizedSearchValue
      ? normalizedSuggestion.includes(normalizedSearchValue)
      : true;
  });
  const shouldShowSuggestions =
    !disabled &&
    isSuggestionOpen &&
    normalizedSearchValue &&
    suggestions.length > 0 &&
    filteredSuggestions.length > 0 &&
    !suggestions.some(
      (suggestion) => String(suggestion).toLowerCase() === normalizedSearchValue
    );

  return (
    <label className={`relative grid content-start gap-1.5 ${wide ? 'md:col-span-2' : ''}`}>
      <span className="text-[0.82rem] font-semibold text-[#17213a]">{label}</span>
      <span
        className={`flex min-h-[42px] items-center gap-3 rounded-md border bg-white px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] ${
          error ? 'border-[#e11d48]' : 'border-[#d8e1ee]'
        }`}
      >
        <Icon className={`h-4 w-4 shrink-0 ${error ? 'text-[#e11d48]' : 'text-[#667391]'}`} aria-hidden="true" />
        <input
          className="min-w-0 flex-1 bg-transparent text-[0.9rem] text-[#17213a] outline-none placeholder:text-[#697594] disabled:text-[#a8b1c5]"
          disabled={disabled}
          inputMode={inputMode}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setIsSuggestionOpen(false)}
          onFocus={() => setIsSuggestionOpen(true)}
          placeholder={placeholder}
        />
      </span>
      {shouldShowSuggestions ? (
        <span className="absolute left-0 right-0 top-[calc(100%-2px)] z-20 overflow-hidden rounded-lg border border-[#d8e1ee] bg-white p-1 shadow-[0_14px_30px_rgba(15,23,42,0.12)]">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              className="block w-full rounded-md px-3 py-2 text-left text-[0.84rem] font-semibold text-[#31446b] transition hover:bg-[#f1f6ff]"
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(suggestion);
              }}
            >
              {suggestion}
            </button>
          ))}
        </span>
      ) : null}
      {error ? <span className="text-[0.74rem] font-semibold text-[#e11d48]">{error}</span> : null}
    </label>
  );
}

function UploadBox({
  title,
  tone,
  proofType,
  disabled,
  preview,
  onBeforeProofBrowse,
  onAfterProofBrowse,
  onProofChange,
  onProofClear,
  onProofPreview,
}) {
  const isCash = tone === 'cash';
  const hasPreview = Boolean(preview);
  const fileInputRef = useRef(null);

  function openFilePicker() {
    if (disabled) {
      return;
    }

    onBeforeProofBrowse?.();
    window.addEventListener(
      'focus',
      () => {
        window.requestAnimationFrame(() => {
          onAfterProofBrowse?.();
        });
      },
      { once: true }
    );
    fileInputRef.current?.click();
  }

  return (
    <div
      className={`mt-4 block rounded-lg border border-dashed bg-white/70 px-4 py-3 transition ${
        isCash ? 'border-[#ffb4bf]' : 'border-[#92dce8]'
      } ${disabled ? 'cursor-not-allowed opacity-70' : 'hover:bg-white'}`}
    >
      <input
        ref={fileInputRef}
        className="hidden"
        disabled={disabled}
        type="file"
        accept="image/png,image/jpeg"
        onChange={onProofChange}
      />
      <p className="m-0 flex items-center gap-2 text-[0.82rem] font-semibold text-[#17213a]">
        {title}
        <span className="grid h-4 w-4 place-items-center rounded-full border border-[#b9c5d8] text-[0.62rem] text-[#31446b]">
          i
        </span>
      </p>
      <div
        className={`mt-3 grid w-full items-center gap-4 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8] sm:grid-cols-[72px_1fr_150px] ${
          disabled ? '' : 'cursor-pointer'
        }`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
          }
        }}
      >
        <div className="grid h-14 w-14 place-items-center rounded-full bg-white text-[#31446b]">
          <CloudUpload className="h-8 w-8" aria-hidden="true" />
        </div>
        <div className="text-center sm:text-left">
          <p className="m-0 text-[0.82rem] text-[#31446b]">
            Drag and drop image here, or click to browse
          </p>
          <p className="m-0 mt-1 text-[0.76rem] text-[#697594]">JPG, PNG up to 5MB</p>
        </div>
        <div className="relative h-[70px] w-[150px] justify-self-center sm:justify-self-end">
          <ProofThumbnail type={proofType} large src={preview} />
          {hasPreview ? (
            <button
              className="absolute inset-0 grid place-items-center rounded-md bg-[color-mix(in_srgb,var(--theme-accent)_18%,transparent)] text-white opacity-100 transition hover:bg-[color-mix(in_srgb,var(--theme-accent)_34%,transparent)] focus-visible:bg-[color-mix(in_srgb,var(--theme-accent)_34%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)]"
              type="button"
              aria-label="Preview proof image"
              title="Click to preview image"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onProofPreview(preview, title.replace(/\s*\(optional\)\s*$/i, ''));
              }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--theme-accent)] px-2.5 py-1 text-[0.72rem] font-semibold text-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.26)] ring-1 ring-white/45">
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                Preview
              </span>
            </button>
          ) : null}
          {hasPreview && !disabled ? (
            <button
              className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full border border-[#ffd1d7] bg-white text-[#e11d48] shadow-[0_8px_18px_rgba(15,23,42,0.16)] transition hover:bg-[#fff1f3]"
              type="button"
              aria-label="Remove proof image"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onProofClear();
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EntryRow({ entry, index, onEdit, onDelete, onPreviewProof }) {
  const isCash = entry.type === 'cash';
  const proofTitle = isCash
    ? 'Proof of Bank Note / Deposit Slip'
    : 'Proof of Stocks Received';

  return (
    <tr
      className={`border-t border-[var(--theme-border-soft)] transition-colors ${
        isCash
          ? 'bg-[#fff1f3] hover:bg-[#ffe5e9]'
          : 'bg-[#effdff] hover:bg-[#def8fb]'
      }`}
    >
      <DailyEntriesTableCell className="text-center">{index}</DailyEntriesTableCell>
      <DailyEntriesTableCell>{formatShortLocalDate(entry.date)}</DailyEntriesTableCell>
      <DailyEntriesTableCell>
        <span className="inline-flex items-center gap-3">
          <TypeBadge type={entry.type} />
          <span className="block max-w-[220px] truncate" title={entry.supplier}>
            {entry.supplier}
          </span>
        </span>
      </DailyEntriesTableCell>
      <DailyEntriesTableCell className="text-center">
        {isCash ? formatPeso(entry.caAmount) : `${entry.quantity} sacks`}
      </DailyEntriesTableCell>
      <DailyEntriesTableCell className="text-center">
        {isCash ? entry.bank : `${formatNumber(entry.netKg)} KG`}
      </DailyEntriesTableCell>
      <DailyEntriesTableCell className="text-center">
        {isCash ? '-' : entry.lansa || '-'}
      </DailyEntriesTableCell>
      <DailyEntriesTableCell>
        <div className="flex justify-center">
          <ProofPreviewButton
            type={isCash ? 'bank' : 'stock'}
            src={entry.proofPreview}
            title={proofTitle}
            isExpired={entry.isProofExpired}
            retention={{
              expiresAt: entry.proofImageExpiresAt,
              daysRemaining: entry.proofImageDaysRemaining,
            }}
            onPreview={onPreviewProof}
          />
        </div>
      </DailyEntriesTableCell>
      <DailyEntriesTableCell>
        <div className="flex justify-center gap-3">
          <IconButton tone="edit" label="Edit entry" icon={PencilLine} onClick={() => onEdit(entry)} />
          <IconButton tone="delete" label="Delete entry" icon={Trash2} onClick={() => onDelete(entry)} />
        </div>
      </DailyEntriesTableCell>
    </tr>
  );
}

function DailyEntriesTableHeading({ children, className = '' }) {
  return (
    <th className={`whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4 ${className}`}>
      {children}
    </th>
  );
}

function DailyEntriesTableCell({ children, className = '' }) {
  return (
    <td className={`whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[#222222] lg:px-4 ${className}`}>
      {children}
    </td>
  );
}

function TypeBadge({ type }) {
  const isCash = type === 'cash';

  return (
    <span className={`rounded-full px-2.5 py-1 text-[0.62rem] font-semibold text-white ${isCash ? 'bg-[#f13b4f]' : 'bg-[#008aa5]'}`}>
      {isCash ? 'Cash Advance' : 'Received Stock'}
    </span>
  );
}

function IconButton({ icon: Icon, label, tone, onClick }) {
  return (
    <button
      className={`grid h-8 w-8 place-items-center rounded-md border bg-white transition ${
        tone === 'delete'
          ? 'border-[#ffb8c0] text-[#ff2439] hover:bg-[#fff1f3]'
          : 'border-[#91bdff] text-[#1267ff] hover:bg-[#f1f6ff]'
      }`}
      type="button"
      aria-label={label}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div className="flex min-w-[150px] items-center overflow-hidden rounded-lg border border-[var(--theme-border-soft)] bg-white shadow-[0_8px_18px_rgba(15,23,42,0.04)]">
      <CustomSelect
        label={label}
        name="dailyEntryTypeFilter"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        options={options}
        searchable={false}
        dropdownMinWidth={220}
        wrapperClassName="relative grid min-w-0 flex-1 gap-0"
        labelClassName="block bg-[linear-gradient(90deg,color-mix(in_srgb,var(--theme-accent-panel)_68%,white)_0%,color-mix(in_srgb,var(--theme-accent-panel)_42%,white)_58%,transparent_100%)] px-2.5 py-1 text-[0.7rem] font-semibold leading-4 text-[var(--theme-accent-panel-text)]"
        triggerClassName="min-h-0 border-0 bg-transparent px-2.5 py-1 text-[0.84rem] font-semibold text-[#31446b] shadow-none focus:ring-0"
        triggerShapeClassName="rounded-none"
      />
    </div>
  );
}

function MonthFilter({ value, onChange }) {
  return (
    <CustomMonthPicker
      label="Filter by Month"
      name="dailyEntryFilterMonth"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      preferredPlacement="auto"
      triggerShapeClassName="rounded-none"
      wrapperClassName="relative grid min-w-[150px] gap-0 rounded-lg border border-[var(--theme-border-soft)] bg-white shadow-[0_8px_18px_rgba(15,23,42,0.04)]"
      labelClassName="block rounded-t-lg bg-[linear-gradient(90deg,color-mix(in_srgb,var(--theme-accent-panel)_68%,white)_0%,color-mix(in_srgb,var(--theme-accent-panel)_42%,white)_58%,transparent_100%)] px-2.5 py-1 text-[0.7rem] font-semibold leading-4 text-[var(--theme-accent-panel-text)]"
      triggerClassName="min-h-0 border-0 bg-transparent px-2.5 py-1 text-[0.84rem] font-semibold text-[#31446b] shadow-none focus:ring-0"
      popupAlign="right"
    />
  );
}

function DateFilter({ value, focusMonth, onChange }) {
  return (
    <CustomDatePicker
      label="Filter by Date"
      name="dailyEntryFilterDate"
      value={value}
      focusMonth={focusMonth}
      onChange={(event) => onChange(event.target.value)}
      preferredPlacement="auto"
      triggerShapeClassName="rounded-none"
      wrapperClassName="relative grid min-w-[150px] gap-0 rounded-lg border border-[var(--theme-border-soft)] bg-white shadow-[0_8px_18px_rgba(15,23,42,0.04)]"
      labelClassName="block rounded-t-lg bg-[linear-gradient(90deg,color-mix(in_srgb,var(--theme-accent-panel)_68%,white)_0%,color-mix(in_srgb,var(--theme-accent-panel)_42%,white)_58%,transparent_100%)] px-2.5 py-1 text-[0.7rem] font-semibold leading-4 text-[var(--theme-accent-panel-text)]"
      triggerClassName="min-h-0 border-0 bg-transparent px-2.5 py-1 text-[0.84rem] font-semibold text-[#31446b] shadow-none focus:ring-0"
      popupAlign="right"
    />
  );
}

function ProofPreviewButton({
  type,
  src,
  title,
  isExpired = false,
  retention = null,
  onPreview,
}) {
  if (isExpired) {
    return (
      <span className="inline-flex min-h-[28px] items-center rounded-full border border-[#fecdd3] bg-[#fff1f2] px-3 text-[0.68rem] font-bold uppercase tracking-[0.08em] text-[#e11d48] shadow-[0_8px_18px_rgba(225,29,72,0.12)]">
        Expired
      </span>
    );
  }

  if (!src) {
    return <ProofThumbnail type={type} src={src} />;
  }

  return (
    <button
      className="group relative rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)]"
      type="button"
      aria-label={`Preview ${title}`}
      title="Click to preview image"
      onClick={() => onPreview(src, title, retention)}
    >
      <ProofThumbnail type={type} src={src} />
      <span className="absolute inset-0 grid place-items-center rounded-md bg-[color-mix(in_srgb,var(--theme-accent)_16%,transparent)] opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--theme-accent)] text-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.22)] ring-1 ring-white/45">
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </span>
    </button>
  );
}

function ProofThumbnail({ type, large = false, src = '' }) {
  if (src) {
    return (
      <img
        className={`rounded-md border border-[#cdd7e6] object-cover ${large ? 'h-[70px] w-[150px]' : 'h-7 w-14'}`}
        src={src}
        alt=""
      />
    );
  }

  if (type === 'bank') {
    return (
      <div
        className={`relative overflow-hidden rounded-md border border-[#cdd7e6] bg-[#d8e4f4] ${large ? 'h-[70px] w-[150px]' : 'h-7 w-14'}`}
        aria-label="Deposit slip proof thumbnail"
      >
        <div className="absolute inset-1 rounded bg-[#edf4fb]" />
        <div className="absolute left-3 top-2 h-1.5 w-12 rounded bg-[#879ab8]" />
        <div className="absolute left-3 top-5 h-1 w-20 rounded bg-[#b0bfd2]" />
        <div className="absolute bottom-2 left-3 h-1 w-16 rounded bg-[#b0bfd2]" />
        <ImageIcon className="absolute right-2 top-2 h-4 w-4 text-[#7890ae]" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-[#b9cabd] bg-[#7e8d76] ${large ? 'h-[70px] w-[150px]' : 'h-7 w-14'}`}
      aria-label="Seaweed sacks proof thumbnail"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,#c9c7ab_0_9%,transparent_10%),radial-gradient(circle_at_45%_55%,#b2ad8d_0_11%,transparent_12%),radial-gradient(circle_at_70%_28%,#d5d0ad_0_10%,transparent_11%),radial-gradient(circle_at_78%_76%,#a7a486_0_12%,transparent_13%),linear-gradient(135deg,#687560,#9da280)]" />
      <div className="absolute inset-x-0 bottom-0 h-3 bg-black/12" />
    </div>
  );
}

function getProofImageRetention(retention, now = new Date()) {
  const expiresAt = retention?.expiresAt ? new Date(retention.expiresAt) : null;
  const remainingMs =
    expiresAt && !Number.isNaN(expiresAt.getTime())
      ? expiresAt.getTime() - now.getTime()
      : null;
  const daysRemaining = Number(retention?.daysRemaining);

  if (remainingMs === null && !Number.isFinite(daysRemaining)) {
    return {
      label: '',
      modalLabel: '',
      tone: 'neutral',
    };
  }

  const normalizedDaysRemaining =
    remainingMs === null ? Math.max(0, daysRemaining) : Math.max(0, Math.ceil(remainingMs / MS_PER_DAY));
  const countdownLabel = formatSelfDestructCountdown(remainingMs);

  return {
    label:
      countdownLabel ||
      (normalizedDaysRemaining <= 0
        ? 'Deletes today'
        : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left`),
    modalLabel:
      countdownLabel
        ? remainingMs <= 0
          ? countdownLabel
          : `${countdownLabel} to self-destruct`
        : normalizedDaysRemaining <= 0
          ? 'Self-destructs today'
          : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left to self-destruct`,
    tone:
      normalizedDaysRemaining <= 3
        ? 'danger'
        : normalizedDaysRemaining <= 7
          ? 'warning'
          : 'success',
  };
}

function formatSelfDestructCountdown(remainingMs) {
  if (!Number.isFinite(remainingMs)) {
    return '';
  }

  if (remainingMs <= 0) {
    return 'Self-destructing now';
  }

  if (remainingMs >= MS_PER_DAY) {
    return '';
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours) {
    parts.push(`${hours}h`);
  }

  if (hours || minutes) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return `${parts.join(' ')} left`;
}

function getEntrySaveConfirmation(values, editingId) {
  const entryLabel = values.type === 'cash' ? 'Cash Advance' : 'Received Stock';

  if (editingId) {
    return {
      title: `Save ${entryLabel} changes?`,
      message: 'Please confirm that you want to save the updated daily entry details.',
      confirmLabel: 'Save Entry',
      confirmingLabel: 'Saving...',
      cancelLabel: 'Review',
    };
  }

  return {
    title: `Add ${entryLabel} entry?`,
    message: `Please confirm that you want to add this ${entryLabel.toLowerCase()} daily entry.`,
    confirmLabel: 'Add Entry',
    confirmingLabel: 'Adding...',
    cancelLabel: 'Review',
  };
}

function validateForm(values) {
  const errors = {};

  if (!values.date) {
    errors.date = 'Date is required.';
  }

  if (!values.supplier.trim()) {
    errors.supplier =
      values.type === 'cash' ? 'Supplier / Requested By is required.' : 'Supplier is required.';
  }

  if (values.type === 'received') {
    if (!values.quantity.trim()) {
      errors.quantity = 'Quantity is required.';
    }

    if (!normalizeNumericPayloadValue(values.netKg).trim()) {
      errors.netKg = 'Net KG is required.';
    }
  }

  if (values.type === 'cash') {
    if (!normalizeNumericPayloadValue(values.caAmount).trim()) {
      errors.caAmount = 'CA amount is required.';
    }

    if (!values.bank.trim()) {
      errors.bank = 'Bank is required.';
    }
  }

  return errors;
}

function formatDailyEntryFieldValue(name, value) {
  if (name === 'quantity') {
    return String(value || '').replace(/\D/g, '');
  }

  if (name === 'netKg') {
    return formatCommaSeparatedDecimalValue(value, 2);
  }

  if (name === 'caAmount') {
    return formatCommaSeparatedDecimalValue(value, 2);
  }

  if (name === 'bank') {
    return String(value || '').toUpperCase();
  }

  return value;
}

function formatCommaSeparatedDecimalValue(value, decimalPlaces = 2) {
  const sanitizedValue = String(value || '').replace(/[^\d.]/g, '');
  const [rawWholePart = '', ...decimalParts] = sanitizedValue.split('.');
  const wholePart = rawWholePart.replace(/^0+(?=\d)/, '');
  const formattedWholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (decimalParts.length) {
    const decimalPart = decimalParts.join('').slice(0, decimalPlaces);

    return formattedWholePart || decimalPart ? `${formattedWholePart}.${decimalPart}` : '';
  }

  return formattedWholePart;
}

function normalizeNumericPayloadValue(value) {
  return String(value || '').replace(/,/g, '').replace(/\.$/, '');
}

function buildDailyEntryPayload(values) {
  const payload = {
    type: values.type,
    date: values.date,
    supplier: values.supplier,
    quantity: values.quantity,
    netKg: normalizeNumericPayloadValue(values.netKg),
    lansa: values.lansa,
    caAmount: normalizeNumericPayloadValue(values.caAmount),
    bank: values.bank,
  };

  if (String(values.proofPreview || '').startsWith('data:')) {
    payload.proofImageData = values.proofPreview;
  }

  if (values.removeProofImage) {
    payload.removeProofImage = true;
  }

  return payload;
}

function formatNumber(value) {
  const numberValue = Number(String(value).replace(/,/g, ''));

  if (!Number.isFinite(numberValue)) {
    return value;
  }

  return numberValue.toLocaleString('en-US');
}

function formatPeso(value) {
  const numberValue = Number(String(value).replace(/,/g, ''));

  if (!Number.isFinite(numberValue)) {
    return `P${value}`;
  }

  return `P${numberValue.toLocaleString('en-US')}`;
}

function formatLongLocalDate(isoDate) {
  const [year, month, day] = String(isoDate || '').split('-').map(Number);

  if (!year || !month || !day) {
    return '';
  }

  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatShortLocalDate(isoDate) {
  const [year, month, day] = String(isoDate || '').split('-').map(Number);

  if (!year || !month || !day) {
    return isoDate || '';
  }

  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
}

export default DailyEntriesPage;
